import { isEmpty } from 'lodash-es';

import { TrackEventTypes, TrackingEventType } from '@/entities/tracking/const';

import { EMPTY_STR } from '../constants/common';
import { ELEMENT_TYPES, OFF, ON } from '../constants/tracking';
import { parseURLEnd } from '../parseURLEnd';

import { filterObject } from './filterObject';
import { getFooterOrNavExtras } from './getFooterOrNavExtras';
import { isFooterOrNav } from './isFooterOrNav';

type FilteredObject = { [key: string]: string };

const normalizeText = (raw: string) =>
    (raw || EMPTY_STR)
        .replace(/\n/g, ' ')
        .replaceAll(/(\,|\?)/g, EMPTY_STR)
        .toLowerCase()
        .trim();

const getFilteredDataset = (element: HTMLElement): FilteredObject => {
    let filteredDataset: FilteredObject = {};
    const dataset = element?.dataset;
    if (dataset) {
        filteredDataset = filterObject(dataset);
        if (isEmpty(filteredDataset)) {
            const tempElement = element.closest('[data-custom-track]') as HTMLElement | null;
            if (tempElement) {
                filteredDataset = filterObject(tempElement.dataset);
            }
        }
    }
    return filteredDataset;
};

const getTrackingInfo = (
    element: HTMLElement,
    elementType: ELEMENT_TYPES,
): {
    text: string;
    trackingType: TrackingEventType;
    filteredDataset: FilteredObject;
    isPrescribedName: boolean;
    customTrackingName?: string;
} => {
    let text = EMPTY_STR;
    let trackingType: TrackingEventType = TrackEventTypes.CLICK;
    let filteredDataset: FilteredObject = {};
    let isPrescribedName = false;
    let customTrackingName: string | undefined;

    if (elementType === ELEMENT_TYPES.TOGGLE) {
        const closestToggle = element.closest(
            '.vds-toggle--outlined__option, .vds-toggle--solid__option',
        ) as HTMLElement | null;

        if (!closestToggle) {
            return {
                text: '',
                trackingType: TrackEventTypes.TOGGLE,
                filteredDataset: {},
                isPrescribedName: false,
                customTrackingName: undefined,
            };
        }

        element = closestToggle;
    }

    const dataset = element?.dataset;

    if (dataset?.prescribedName) {
        isPrescribedName = true;
        customTrackingName = dataset.prescribedName;
    }

    filteredDataset = getFilteredDataset(element);

    const footerOrNav = isFooterOrNav(element);
    if (footerOrNav) {
        const footerNavExtras = getFooterOrNavExtras(footerOrNav, element);
        filteredDataset = { ...(filteredDataset || {}), ...footerNavExtras };
    }

    if (dataset?.prescribedName) {
        isPrescribedName = true;
        customTrackingName = dataset.prescribedName;
    }

    if (elementType === ELEMENT_TYPES.BUTTON || elementType === ELEMENT_TYPES.LINK) {
        if (elementType === ELEMENT_TYPES.BUTTON) {
            trackingType = TrackEventTypes.BUTTON_CLICK;
        }
        let ctaText = normalizeText(element.innerText || element.textContent || EMPTY_STR);

        if (!filteredDataset?.ctaText) {
            if (elementType === ELEMENT_TYPES.LINK) {
                const linkElement = element as HTMLAnchorElement;
                const href = linkElement?.href;
                if (href) {
                    const lastWord = parseURLEnd(href);
                    if (lastWord !== href) ctaText = normalizeText(lastWord);
                }
            }
            filteredDataset = { ...(filteredDataset || {}), ctaText };
            text = ctaText;
        } else {
            text = normalizeText(filteredDataset.ctaText!);
        }
    } else if (elementType === ELEMENT_TYPES.TOGGLE) {
        trackingType = TrackEventTypes.TOGGLE;

        const dataset = element.dataset;
        const rawName = dataset?.toggleName || 'toggle';
        const labelText = normalizeText(element.innerText || element.textContent || EMPTY_STR);
        const ctaText = labelText || normalizeText(rawName);

        filteredDataset = { ...(getFilteredDataset(element) || {}), ctaText };
        text = ctaText;
        filteredDataset = { ...(filteredDataset || {}), ctaText };
    } else if (elementType === ELEMENT_TYPES.SWITCH) {
        trackingType = TrackEventTypes.SWITCH;

        if (dataset?.prescribedName) {
            isPrescribedName = true;
            customTrackingName = dataset.prescribedName;
        }

        const input = element.nextSibling as HTMLInputElement;
        const checked = input?.checked;
        const name = input?.id;
        text = `${name}_${checked ? ON : OFF}`;
    }

    return {
        text,
        trackingType,
        filteredDataset,
        isPrescribedName,
        customTrackingName,
    };
};

export { getTrackingInfo };
